/**
 * displayFormat.ts — 一覧表示用の省略表示ユーティリティ
 *
 * もともと ReservationTable.tsx(デスクトップ貸出返却リスト)のローカル関数だったものを、
 * モバイルPWA(/mobile)のカード表示でも同じ省略体験を提供するために共有化した。
 * 表示ロジックはデスクトップ版と完全に同一に保つこと(片方だけ変えると
 * PC とスマホで同じ車両が違う表記になり現場が混乱する)。
 */
import type { CSSProperties } from 'react';

// ステータス名を頭3文字に切り詰める(列を揃えるため)。省略記号(…)は付けない
// (例: 予約確定→「予約確」)。全文はTooltip等で補う。
//
// 2026-08-25: 以前は2文字を全角スペースで割って「店　舗」と3文字幅に見せていたが、
// ブラウザの検索(Cmd+F)で「店舗」がヒットしなくなるため廃止した。
// 3文字幅への広げは spacedLabelStyle() の CSS(letter-spacing) 側で行う。
export const truncStatusName = (s: any): string => {
  const t = String(s ?? '');
  return t.length >= 3 ? t.slice(0, 3) : t;
};

// 固定幅のチップ/ボタンの中で、2文字ラベルを3文字ぶんの幅に見せる **内側 span 用** スタイル。
// DOM 上のテキストは元のまま(「店舗」)なのでブラウザ検索(Cmd+F)でヒットする。
// 2文字以外は undefined(素の中央寄せのままでよい)。
//
// letter-spacing は **最後の1文字の後ろにも** 入る。この余分な字間のせいで行の幅が
// 箱の内寸を超えてしまい、text-align: center が効かず右へはみ出す
// (実測: font 0.7rem / 箱の内寸 41.4px に対し 2文字+字間2em = 44.8px)。
// そこで **margin-right に同じ値のマイナス** を入れて末尾の字間を打ち消す。
// 行の幅が 2文字+字間1em = 3文字ぶん になり、通常の中央寄せで3文字ラベルと
// ぴったり同じ位置に揃う(実測: 左余白 5.9px = 3文字ラベルと同値)。
//
// ※ text-indent で半分ぶん押し戻す方法は不可。上記のとおり中央寄せ自体が
//   効いていない状態なので、指定ぶんだけ右に寄って余計にずれる。
const LABEL_LETTER_SPACING = '1em'; // 2文字 + 字間1em = 3文字ぶんの見た目

export const spacedLabelStyle = (label: any): CSSProperties | undefined => {
  const t = String(label ?? '');
  if (t.length !== 2) return undefined;
  return { letterSpacing: LABEL_LETTER_SPACING, marginRight: `-${LABEL_LETTER_SPACING}` };
};

// 車両コードの末尾番号(最後の数字連続)だけを取り出す。区切り文字(-, 全角ダッシュ等)に
// 依存せず、末尾4桁を返す。例: M-D5-B-8085 → 8085 / T-AL-B-698 → 698 / 12345 → 2345
export const shortVehicleCode = (code: any): string => {
  const c = String(code ?? '');
  if (!c) return '';
  const nums = c.match(/\d+/g);
  if (!nums || nums.length === 0) return c; // 数字が無ければ全体を表示
  const last = nums[nums.length - 1];
  return last.length > 4 ? last.slice(-4) : last;
};

/**
 * 全角数字（iPhone の日本語キーボードは数字が全角で入ることがある）を半角にし、数字以外を捨てる。
 * PWA 車両一覧の車両番号検索で使う（"９８６５" → "9865"。2026-10-02）
 */
export const toDigits = (s: string): string => String(s ?? '').normalize('NFKC').replace(/[^0-9]/g, '');
